import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Book, CheckCircle2, AlertTriangle, Clock,
  ChevronRight, ChevronDown, UserX,
} from "lucide-react";
import apiFetch from "../apiFetch";
import HomeworkReportModal from "../components/HomeworkReportModal";
import { tierLabel as vocabTierLabel } from "../data/vocabYearGroups";
import "../styles/homeworkPage.css";

const GAME_LABELS = {
  comprehension:      "Comprehension",
  mcq:                "MCQ Master",
  spot_word:          "Spot the Word",
  ranked:             "Ranked",
  book_comprehension: "Book Comprehension",
  book_reading:       "Book Reading",
  vocab_definitions:  "Vocab: Definitions",
  vocab_synonyms:     "Vocab: Synonyms",
  vocab_antonyms:     "Vocab: Antonyms",
  vocab_mixed:        "Vocab: Mixed",
  synonyms_game:      "Synonyms Game",
  antonyms_game:      "Antonyms Game",
  vocab_matching:     "Vocab Matching",
};

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function StatusBadge({ status }) {
  if (status === "completed") return <span className="hw-badge hw-badge--green"><CheckCircle2 size={13} /> Done</span>;
  if (status === "overdue")   return <span className="hw-badge hw-badge--red"><AlertTriangle size={13} /> Overdue</span>;
  return <span className="hw-badge hw-badge--grey"><Clock size={13} /> Pending</span>;
}

function HomeworkCard({ hw, onPlay, onReport }) {
  const params = hw.parameters_json || {};
  const gameLabel = GAME_LABELS[hw.game_type] || hw.game_type;

  const tags = [
    gameLabel,
    params.tierName       || null,
    params.topicCategory  || null,
    params.tier != null ? vocabTierLabel(params.tier) : null,
    params.wordCount      ? `${params.wordCount} words` : null,
    params.targetCorrect  ? `${params.targetCorrect} correct` : null,
  ].filter(Boolean);

  return (
    <div
      className={`hw-card hw-card--${hw.status}${hw.status === "completed" ? " hw-card--report" : ""}`}
      onClick={hw.status === "completed" ? () => onReport(hw.id) : undefined}
      role={hw.status === "completed" ? "button" : undefined}
      tabIndex={hw.status === "completed" ? 0 : undefined}
      onKeyDown={hw.status === "completed" ? e => e.key === "Enter" && onReport(hw.id) : undefined}
    >
      <div className="hw-card-top">
        <StatusBadge status={hw.status} />
        <div className="hw-card-progress">
          <div className="hw-progress-bar">
            <div
              className="hw-progress-fill"
              style={{ width: `${Math.min(100, (hw.completed_count / Math.max(1, hw.total_count)) * 100)}%` }}
            />
          </div>
          <span className="hw-progress-text">{hw.completed_count}/{hw.total_count}</span>
        </div>
      </div>

      <h3 className="hw-card-title">{hw.title}</h3>

      <div className="hw-card-tags">
        {tags.map(t => <span key={t} className="hw-tag">{t}</span>)}
      </div>

      <div className="hw-card-footer">
        <div className="hw-card-meta">
          <span>Set by {hw.teacher_name}</span>
          {hw.due_date && <span className={`hw-due${hw.status === "overdue" ? " hw-due--overdue" : ""}`}>
            Due {fmtDate(hw.due_date)}
          </span>}
        </div>
        {hw.status !== "completed" && (
          <button className="hw-btn hw-btn--primary hw-btn--sm" onClick={() => onPlay(hw)}>
            Start <ChevronRight size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

function CompletedHomeworkRow({ hw, onReport }) {
  const [expanded, setExpanded] = useState(false);
  const params = hw.parameters_json || {};
  const gameLabel = GAME_LABELS[hw.game_type] || hw.game_type;

  const tags = [
    gameLabel,
    params.tierName       || null,
    params.topicCategory  || null,
    params.tier != null ? vocabTierLabel(params.tier) : null,
    params.wordCount      ? `${params.wordCount} words` : null,
    params.targetCorrect  ? `${params.targetCorrect} correct` : null,
  ].filter(Boolean);

  return (
    <div className={`hw-collapsed-row${expanded ? " hw-collapsed-row--open" : ""}`}>
      <button
        className="hw-collapsed-summary"
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
      >
        <span className="hw-badge hw-badge--green hw-collapsed-badge"><CheckCircle2 size={13} /> Done</span>
        <span className="hw-collapsed-title">{hw.title}</span>
        <span className="hw-collapsed-meta">
          <span className="hw-tag">{gameLabel}</span>
          {hw.due_date && <span className="hw-due">Due {fmtDate(hw.due_date)}</span>}
        </span>
        {expanded ? <ChevronDown size={16} className="hw-collapsed-chevron" /> : <ChevronRight size={16} className="hw-collapsed-chevron" />}
      </button>

      {expanded && (
        <div className="hw-collapsed-detail">
          <div className="hw-card-tags">
            {tags.map(t => <span key={t} className="hw-tag">{t}</span>)}
          </div>
          <div className="hw-collapsed-footer">
            <span className="hw-card-meta">Set by {hw.teacher_name}</span>
            <button className="hw-btn hw-btn--primary hw-btn--sm" onClick={() => onReport(hw.id)}>
              View Report <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function HomeworkPage() {
  const navigate = useNavigate();
  const [connection,  setConnection]  = useState(null);
  const [homework,    setHomework]    = useState([]);
  const [status,      setStatus]      = useState("loading");
  const [reportId,    setReportId]    = useState(null);
  const [confirming,  setConfirming]  = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [connRes, hwRes] = await Promise.all([
        apiFetch("/api/student/connection"),
        apiFetch("/api/student/homework"),
      ]);
      if (connRes.status === 401) { setStatus("unauth"); return; }
      if (!connRes.ok || !hwRes.ok) { setStatus("error"); return; }
      const [conn, hw] = await Promise.all([connRes.json(), hwRes.json()]);
      setConnection(conn);
      setHomework(Array.isArray(hw) ? hw : []);
      setStatus("ok");
    } catch {
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    document.title = "Homework — Owllet Reading Quest";
    fetchData();
  }, [fetchData]);

  const handleDisconnect = async () => {
    await apiFetch("/api/student/disconnect", { method: "DELETE" });
    navigate("/");
  };

  const handlePlay = (hw) => {
    const p = hw.parameters_json || {};
    // Store only the assignment ID — game params go via location.state
    sessionStorage.setItem("hw_params", JSON.stringify({ assignmentId: hw.id }));
    sessionStorage.setItem("returnTo", "/homework");

    const base = { fromGameSelect: true };

    switch (hw.game_type) {
      case "comprehension":
        navigate("/comprehensionGame", { state: { ...base, tier: p.tier, tierName: p.tierName, topicCategory: p.topicCategory } });
        break;
      case "mcq":
        navigate("/mcq10Game", { state: { ...base, tier: p.tier, tierName: p.tierName, topicCategory: p.topicCategory } });
        break;
      case "spot_word":
        navigate("/spotWordGame", { state: { ...base, tier: p.tier, tierName: p.tierName, topicCategory: p.topicCategory } });
        break;
      case "ranked":
        navigate("/rankedGame", { state: base });
        break;
      case "synonyms_game":
        navigate("/synonymsGame", { state: base });
        break;
      case "antonyms_game":
        navigate("/antonymsGame", { state: base });
        break;
      case "vocab_matching":
        navigate("/vocabMatchingGame", { state: base });
        break;
      case "vocab_definitions":
      case "vocab_synonyms":
      case "vocab_antonyms":
      case "vocab_mixed":
        navigate("/vocab-test/game", { state: {
          testType:  p.testType  || hw.game_type.replace("vocab_", ""),
          wordCount: p.wordCount || 10,
          // p.tier is set by new assignments; p.startTier is from legacy assignments
          tier: p.tier ?? p.startTier ?? 0,
        }});
        break;
      case "book_comprehension":
        if (p.bookId && p.chapterId) {
          navigate("/bookBasedComprehensionGame", { state: {
            ...base,
            bookId:      p.bookId,
            chapterId:   p.chapterId,
            bookTitle:   p.bookTitle   || p.bookId,
            chapterDesc: p.chapterDesc || "",
            questionType: p.questionType || "mcq",
          }});
        } else {
          // Legacy assignment — let student pick their own book
          navigate("/bookSelector", { state: base });
        }
        break;
      case "book_reading":
        if (p.bookId && p.chapterId) {
          navigate("/chapterReadingPage", { state: {
            ...base,
            bookId:        p.bookId,
            chapterId:     p.chapterId,
            bookTitle:     p.bookTitle   || p.bookId,
            chapterDesc:   p.chapterDesc || "",
            chapterIndex:  p.chapterIndex  ?? 0,
            totalChapters: p.totalChapters ?? 1,
            allChapters:   [],
          }});
        } else {
          // Legacy assignment — let student pick their own book
          navigate("/bookReading", { state: base });
        }
        break;
      default:
        navigate("/home");
    }
  };

  if (status === "loading") return <div className="hw-page"><div className="hw-loading">Loading homework…</div></div>;
  if (status === "unauth")  return <div className="hw-page"><div className="hw-loading">Please log in first. <Link to="/">Home</Link></div></div>;
  if (status === "error")   return <div className="hw-page"><div className="hw-loading">Failed to load. <button onClick={fetchData}>Retry</button></div></div>;

  const pending   = homework.filter(h => h.status === "pending");
  const overdue   = homework.filter(h => h.status === "overdue");

  const VOCAB_TYPES = new Set(["vocab_definitions","vocab_synonyms","vocab_antonyms","vocab_mixed","synonyms_game","antonyms_game","vocab_matching","spot_word"]);
  const BOOK_TYPES  = new Set(["book_comprehension", "book_reading"]);
  function hwMins(h) {
    const remaining = Math.max(0, h.total_count - h.completed_count);
    if (VOCAB_TYPES.has(h.game_type)) return Math.ceil(remaining * 0.5);
    if (BOOK_TYPES.has(h.game_type))  return 10;
    return 7;
  }
  const pendingMins = pending.reduce((sum, h) => sum + hwMins(h), 0);

  const completed = homework
    .filter(h => h.status === "completed")
    .sort((a, b) => new Date(b.assigned_at) - new Date(a.assigned_at));

  return (
    <div className="hw-page">
      <header className="hw-header">
        <div className="hw-header-left">
          <div className="hw-header-logo"><Book size={24} color="#fff" /></div>
          <div>
            <h1 className="hw-header-title">Homework</h1>
            {connection?.status === "accepted" && (
              <p className="hw-header-sub">Class: {connection.teacherName}</p>
            )}
          </div>
        </div>
        <div className="hw-header-center">
          <p className="hw-header-motivate">Finish your tasks and level up your skills!</p>
        </div>
        <div className="hw-header-right">
          {connection?.status === "accepted" && (
            confirming ? (
              <div className="hw-leave-confirm">
                <span className="hw-leave-warn">Leaving the class will remove you from your tutor, do you want to leave the tutor?</span>
                <button className="hw-btn hw-btn--danger hw-btn--sm" onClick={handleDisconnect}>Yes, Leave</button>
                <button className="hw-btn hw-btn--ghost hw-btn--sm" onClick={() => setConfirming(false)}>Cancel</button>
              </div>
            ) : (
              <button className="hw-btn hw-btn--danger hw-btn--sm" onClick={() => setConfirming(true)}>
                <UserX size={15} /> Leave Class
              </button>
            )
          )}
        </div>
      </header>

      <main className="hw-main">
        {/* Pending connection */}
        {connection?.status === "pending" && (
          <div className="hw-notice hw-notice--amber">
            Your request to join {connection.teacherName}'s class is awaiting approval.
          </div>
        )}

        {/* No connection */}
        {(!connection || connection.status === "none") && (
          <div className="hw-notice hw-notice--grey">
            You are not connected to a class. <Link to="/join-class">Join a class</Link> to receive assignments.
          </div>
        )}

        {/* Overdue */}
        {overdue.length > 0 && (
          <section className="hw-section">
            <h2 className="hw-section-title hw-section-title--red"><AlertTriangle size={22} /> Overdue</h2>
            <div className="hw-grid">
              {overdue.map(h => <HomeworkCard key={h.id} hw={h} onPlay={handlePlay} />)}
            </div>
          </section>
        )}

        {/* Pending */}
        {pending.length > 0 && (
          <section className="hw-section">
            <h2 className="hw-section-title">
              <Clock size={22} /> Pending Tasks
              {pendingMins > 0 && <span className="hw-time-estimate">~{pendingMins} min</span>}
            </h2>
            <div className="hw-grid">
              {pending.map(h => <HomeworkCard key={h.id} hw={h} onPlay={handlePlay} />)}
            </div>
          </section>
        )}

        {/* Completed */}
        {completed.length > 0 && (
          <section className="hw-section">
            <h2 className="hw-section-title hw-section-title--green"><CheckCircle2 size={22} /> Completed</h2>
            <div className="hw-completed-list">
              {completed.map(h => <CompletedHomeworkRow key={h.id} hw={h} onReport={setReportId} />)}
            </div>
          </section>
        )}

        {/* Empty */}
        {connection?.status === "accepted" && homework.length === 0 && (
          <div className="hw-empty">
            <CheckCircle2 size={48} className="hw-empty-icon" />
            <p>No assignments yet — check back later.</p>
          </div>
        )}
      </main>

      <HomeworkReportModal
        assignmentId={reportId}
        role="student"
        onClose={() => setReportId(null)}
      />
    </div>
  );
}
